import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    downloadMotivationExport,
    loadCurrentUser,
    loadMetadata,
    loadMotivation,
    saveMotivationEngineerInn,
    saveMotivationDeduction,
} from './api';
import SearchableSelect from './SearchableSelect';
import { WeakPerformanceHistoryModal, WeakStatusButton } from './WeakPerformanceHistoryModal';


const today = new Date().toISOString().slice(0, 10);
const monthStart = `${today.slice(0, 8)}01`;
const initialFilters = {
    start_date: monthStart,
    end_date: today,
    location: '',
};

const fallbackLocations = [
    { id: 1, city: 'Тюмень', address: 'Локальный пример' },
    { id: 2, city: 'Москва', address: 'Локальный пример' },
];

const fallbackMotivation = {
    period: {
        start_date: monthStart,
        end_date: today,
        location: '',
        work_days: 21,
        norm_start_date: monthStart,
        norm_end_date: today,
    },
    permissions: {
        can_manage_deductions: true,
    },
    rules: {
        modules: { norm_minutes: 200, reward_amount: 650 },
        cassettes: { norm_per_day_units: 12, reward_amount: 790 },
        overrides: [
            { category: 'modules', location: 'Москва', norm_minutes: 200, reward_amount: 0 },
            { category: 'cassettes', location: 'Москва', norm_per_day_units: 12, reward_amount: 970 },
        ],
    },
    summary: {
        engineer_count: 7,
        module_engineer_count: 7,
        cassette_engineer_count: 1,
        module_fact_total: 347,
        cassette_fact_total: 166,
        module_bonus_display: '66 930 р',
        cassette_bonus_display: '7 900 р',
        total_bonus_display: '74 830 р',
    },
    module_rows: [
        {
            engineer: 'Арефьев Вадим Дмитриевич',
            location: 'Тюмень',
            fact_count: 61,
            work_days: 16,
            period_work_days: 21,
            base_period_work_days: 21,
            deducted_days: 0,
            monthly_norm_units: 50.4,
            over_norm_units: 10.6,
            norm_minutes: 200,
            reward_amount: 650,
            bonus_display: '6 890 р',
        },
        {
            engineer: 'Байкулов Егор',
            location: 'Тюмень',
            fact_count: 80,
            work_days: 17,
            period_work_days: 21,
            base_period_work_days: 21,
            deducted_days: 0,
            monthly_norm_units: 50.4,
            over_norm_units: 29.6,
            norm_minutes: 200,
            reward_amount: 650,
            bonus_display: '19 240 р',
        },
    ],
    cassette_rows: [
        {
            engineer: 'Попов Андрей',
            location: 'Тюмень',
            fact_count: 166,
            work_days: 13,
            period_work_days: 21,
            base_period_work_days: 21,
            deducted_days: 0,
            monthly_norm_units: 252,
            over_norm_units: 0,
            norm_per_day_units: 12,
            reward_amount: 790,
            bonus_display: '0 р',
        },
    ],
};


function formatValue(value) {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    return value;
}

function NumberCell({ value }) {
    return <span className="shell-cellNumber">{value}</span>;
}

function buildWeakKey(engineer, location) {
    return `${engineer}::${location}`;
}

function WeakBadge({ label, persistent = false }) {
    if (!label) {
        return null;
    }

    return (
        <span className={`shell-warningBadge ${persistent ? 'shell-warningBadge--persistent' : ''}`}>
            {label}
        </span>
    );
}

function EngineerCell({ row }) {
    return (
        <div className="shell-identityCell">
            <strong>{row.engineer}</strong>
            {row.weak_label ? (
                <WeakBadge label={row.weak_label} persistent={row.is_persistent_weak} />
            ) : null}
        </div>
    );
}

function DeductionInput({ row, disabled, onSave }) {
    const [value, setValue] = useState(String(row.deducted_days ?? 0));

    useEffect(() => {
        setValue(String(row.deducted_days ?? 0));
    }, [row.deducted_days]);

    function submit() {
        const normalized = value === '' ? '0' : value;
        if (String(row.deducted_days ?? 0) === normalized) {
            return;
        }
        onSave(row, normalized);
    }

    if (disabled) {
        return <NumberCell value={row.deducted_days ?? 0} />;
    }

    return (
        <input
            className="shell-tableInput shell-tableInput--narrow"
            type="number"
            min="0"
            step="0.5"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onBlur={submit}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    event.currentTarget.blur();
                }
            }}
            aria-label="Вычет рабочих дней"
        />
    );
}

function EngineerInnInput({ row, disabled, onSave }) {
    const [value, setValue] = useState(row.inn || '');

    useEffect(() => {
        setValue(row.inn || '');
    }, [row.inn]);

    function submit() {
        if ((row.inn || '') === value) {
            return;
        }
        onSave(row, value);
    }

    return (
        <input
            className="shell-tableInput"
            inputMode="numeric"
            maxLength="12"
            value={value}
            disabled={disabled}
            placeholder="10 или 12 цифр"
            onChange={(event) => setValue(event.target.value.replace(/\D/g, '').slice(0, 12))}
            onBlur={submit}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    event.currentTarget.blur();
                }
            }}
            aria-label={`ИНН: ${row.engineer}`}
        />
    );
}

function RuleCard({ title, primary, secondary, note }) {
    return (
        <div className="shell-pageSummaryCard shell-pageSummaryCard--compact shell-pageSummaryCard--rule">
            <span>{title}</span>
            <strong>{primary}</strong>
            {secondary ? <small>{secondary}</small> : null}
            {note ? <p>{note}</p> : null}
        </div>
    );
}


function MotivationTable({ title, eyebrow, rows, emptyText, columns }) {
    return (
        <section className="shell-section">
            <div className="shell-sectionHeader">
                <div>
                    <span className="shell-sectionEyebrow">{eyebrow}</span>
                    <h3>{title}</h3>
                </div>
                <span>{rows.length} строк</span>
            </div>

            {rows.length ? (
                <div className="shell-tableWrap">
                    <table className="shell-table">
                        <thead>
                            <tr>
                                {columns.map((column) => (
                                    <th key={column.key}>{column.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr
                                    key={`${row.engineer}-${row.location}-${index}`}
                                    className={`shell-tableRow ${row.is_persistent_weak ? 'shell-tableRow--danger' : row.is_weak ? 'shell-tableRow--warning' : ''}`.trim()}
                                >
                                    {columns.map((column) => (
                                        <td key={column.key}>{column.render ? column.render(row) : row[column.key]}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="shell-emptyState shell-emptyState--compact">{emptyText}</div>
            )}
        </section>
    );
}

function WeakCombinedTable({ rows, emptyText, onOpenHistory }) {
    return (
        <section className="shell-section">
            <div className="shell-sectionHeader">
                <div>
                    <span className="shell-sectionEyebrow">Контроль темпа</span>
                    <h3>Кому нужно нарастить темп</h3>
                </div>
                <span>{rows.length ? `${rows.length} строк` : 'Нет записей'}</span>
            </div>

            {rows.length ? (
                <div className="shell-tableWrap shell-tableWrap--weakSummary">
                    <table className="shell-table shell-table--compactSummary">
                        <thead>
                            <tr>
                                <th>Локация</th>
                                <th>Инженер</th>
                                <th>Тип</th>
                                <th>Темп / день</th>
                                <th>Метка</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((item) => (
                                <tr
                                    key={`${item.category}-${buildWeakKey(item.engineer, item.location)}`}
                                    className={`shell-tableRow ${item.status === 'persistent' ? 'shell-tableRow--danger' : 'shell-tableRow--warning'}`.trim()}
                                >
                                    <td>{item.location}</td>
                                    <td className="shell-tableCell--engineerName" title={item.engineer}>{item.engineer}</td>
                                    <td>{item.category === 'modules' ? 'Модули' : 'Кассеты'}</td>
                                    <td className="shell-tableCell--pace">
                                        <strong className="shell-cellNumber">{item.avg_per_day}</strong>
                                        <span>порог {formatValue(item.weak_threshold ?? item.threshold_per_day)}</span>
                                    </td>
                                    <td>
                                        <WeakStatusButton row={item} onClick={onOpenHistory} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="shell-emptyState shell-emptyState--compact">{emptyText}</div>
            )}
        </section>
    );
}


export default function MotivationPage() {
    const [filters, setFilters] = useState(initialFilters);
    const [locations, setLocations] = useState(fallbackLocations);
    const [state, setState] = useState({
        data: fallbackMotivation,
        loading: true,
        warning: '',
    });
    const [deductionState, setDeductionState] = useState({
        saving: false,
        bulkValue: '',
        message: '',
    });
    const [exportState, setExportState] = useState({
        loading: false,
        error: '',
    });
    const [innState, setInnState] = useState({
        savingKey: '',
        message: '',
        error: '',
    });
    const [selectedWeakRow, setSelectedWeakRow] = useState(null);

    useEffect(() => {
        let cancelled = false;

        async function bootstrap() {
            try {
                const [metadata, currentUser] = await Promise.all([
                    loadMetadata(),
                    loadCurrentUser(),
                ]);

                const defaultLocation = currentUser?.location?.city || '';
                const appliedFilters = {
                    ...initialFilters,
                    location: defaultLocation,
                };
                const payload = await loadMotivation(appliedFilters);

                if (cancelled) {
                    return;
                }

                setLocations(metadata.locations || fallbackLocations);
                setFilters(appliedFilters);
                setState({
                    data: payload,
                    loading: false,
                    warning: '',
                });
            } catch (_error) {
                if (cancelled) {
                    return;
                }

                setLocations(fallbackLocations);
                setState({
                    data: fallbackMotivation,
                    loading: false,
                    warning: 'Не удалось загрузить мотивацию. Показан локальный пример.',
                });
            }
        }

        bootstrap();

        return () => {
            cancelled = true;
        };
    }, []);

    async function submitFilters(event) {
        event.preventDefault();
        setState((current) => ({
            ...current,
            loading: true,
        }));

        try {
            const payload = await loadMotivation(filters);
            setState({
                data: payload,
                loading: false,
                warning: '',
            });
        } catch (_error) {
            setState({
                data: fallbackMotivation,
                loading: false,
                warning: 'Не удалось загрузить мотивацию. Показан локальный пример.',
            });
        }
    }

    async function downloadExport() {
        setExportState({ loading: true, error: '' });
        try {
            await downloadMotivationExport(filters);
            setExportState({ loading: false, error: '' });
        } catch (_error) {
            setExportState({
                loading: false,
                error: 'Не удалось сформировать файл мотивации.',
            });
        }
    }

    async function saveEngineerInn(row, inn) {
        const savingKey = `${row.engineer}::${row.location}`;
        setInnState({
            savingKey,
            message: '',
            error: '',
        });
        try {
            const payload = await saveMotivationEngineerInn({
                start_date: filters.start_date,
                end_date: filters.end_date,
                location: filters.location,
                entries: [{
                    engineer: row.engineer,
                    location: row.location,
                    inn,
                }],
            });
            setState({
                data: payload,
                loading: false,
                warning: '',
            });
            setInnState({
                savingKey: '',
                message: inn ? `ИНН для ${row.engineer} сохранен.` : `ИНН для ${row.engineer} очищен.`,
                error: '',
            });
        } catch (error) {
            setInnState({
                savingKey: '',
                message: '',
                error: error?.response?.data?.detail || 'Не удалось сохранить ИНН.',
            });
        }
    }

    const saveDeductions = useCallback(async (entries) => {
        setDeductionState((current) => ({
            ...current,
            saving: true,
            message: '',
        }));

        try {
            const payload = await saveMotivationDeduction({
                start_date: filters.start_date,
                end_date: filters.end_date,
                location: filters.location,
                entries,
            });
            setState({
                data: payload,
                loading: false,
                warning: '',
            });
            setDeductionState((current) => ({
                ...current,
                saving: false,
                message: 'Вычет дней сохранен.',
            }));
        } catch (_error) {
            setDeductionState((current) => ({
                ...current,
                saving: false,
                message: 'Не удалось сохранить вычет дней.',
            }));
        }
    }, [filters.end_date, filters.location, filters.start_date]);

    const saveRowDeduction = useCallback((row, deductedDays) => {
        saveDeductions([{
            engineer: row.engineer,
            location: row.location,
            deducted_days: deductedDays,
        }]);
    }, [saveDeductions]);

    function uniqueDeductionRows() {
        const seen = new Set();
        return [...(state.data.module_rows || []), ...(state.data.cassette_rows || [])].filter((row) => {
            const key = `${row.engineer}::${row.location}`;
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }

    function applyBulkDeduction() {
        const value = deductionState.bulkValue === '' ? '0' : deductionState.bulkValue;
        saveDeductions(uniqueDeductionRows().map((row) => ({
            engineer: row.engineer,
            location: row.location,
            deducted_days: value,
        })));
    }

    const summaryCards = useMemo(() => ([
        { label: 'Инженеров', value: state.loading ? '...' : state.data.summary.engineer_count },
        { label: 'Выработка модулей', value: state.loading ? '...' : state.data.summary.module_fact_total },
        { label: 'Выработка кассет', value: state.loading ? '...' : state.data.summary.cassette_fact_total },
        { label: 'Премия по модулям', value: state.loading ? '...' : state.data.summary.module_bonus_display },
        { label: 'Премия по кассетам', value: state.loading ? '...' : state.data.summary.cassette_bonus_display },
        { label: 'Итого премия', value: state.loading ? '...' : state.data.summary.total_bonus_display },
    ]), [state.data, state.loading]);

    const weakModules = useMemo(
        () => state.data.weak_performance?.modules_by_location || [],
        [state.data.weak_performance],
    );
    const weakCassettes = useMemo(
        () => state.data.weak_performance?.cassettes_by_location || [],
        [state.data.weak_performance],
    );
    const weakRows = useMemo(
        () => [...weakModules, ...weakCassettes].sort((left, right) => {
            const leftPersistent = left.status === 'persistent' ? 0 : 1;
            const rightPersistent = right.status === 'persistent' ? 0 : 1;
            if (leftPersistent !== rightPersistent) {
                return leftPersistent - rightPersistent;
            }
            if (left.location !== right.location) {
                return left.location.localeCompare(right.location, 'ru');
            }
            return String(left.avg_per_day).localeCompare(String(right.avg_per_day), 'ru', { numeric: true });
        }),
        [weakModules, weakCassettes],
    );

    const moduleColumns = useMemo(() => ([
        { key: 'engineer', label: 'ФИО', render: (row) => <EngineerCell row={row} /> },
        { key: 'location', label: 'Локация' },
        { key: 'fact_count', label: 'Факт выработки' },
        { key: 'base_period_work_days', label: 'Раб. дней месяца' },
        {
            key: 'deducted_days',
            label: 'Вычет дней',
            render: (row) => (
                <DeductionInput
                    row={row}
                    disabled={!state.data.permissions?.can_manage_deductions || deductionState.saving}
                    onSave={saveRowDeduction}
                />
            ),
        },
        { key: 'period_work_days', label: 'Дней в плане' },
        { key: 'monthly_norm_units', label: 'Норма модулей в месяц' },
        { key: 'over_norm_units', label: 'Сверх нормы' },
        { key: 'norm_minutes', label: 'Норма минут', render: (row) => <NumberCell value={row.norm_minutes} /> },
        { key: 'reward_amount', label: 'Вознаграждение', render: (row) => <NumberCell value={`${row.reward_amount} р`} /> },
        { key: 'bonus_display', label: 'Премия', render: (row) => <NumberCell value={row.bonus_display} /> },
    ]), [deductionState.saving, saveRowDeduction, state.data.permissions?.can_manage_deductions]);

    const cassetteColumns = useMemo(() => ([
        { key: 'engineer', label: 'ФИО', render: (row) => <EngineerCell row={row} /> },
        { key: 'location', label: 'Локация' },
        { key: 'base_period_work_days', label: 'Раб. дней месяца' },
        {
            key: 'deducted_days',
            label: 'Вычет дней',
            render: (row) => (
                <DeductionInput
                    row={row}
                    disabled={!state.data.permissions?.can_manage_deductions || deductionState.saving}
                    onSave={saveRowDeduction}
                />
            ),
        },
        { key: 'period_work_days', label: 'Дней в плане' },
        { key: 'norm_per_day_units', label: 'Норма в день', render: (row) => <NumberCell value={row.norm_per_day_units} /> },
        { key: 'monthly_norm_units', label: 'Норма в месяц', render: (row) => <NumberCell value={row.monthly_norm_units} /> },
        { key: 'fact_count', label: 'Факт выработки', render: (row) => <NumberCell value={row.fact_count} /> },
        { key: 'over_norm_units', label: 'Сверх нормы', render: (row) => <NumberCell value={row.over_norm_units} /> },
        { key: 'reward_amount', label: 'Вознаграждение', render: (row) => <NumberCell value={`${row.reward_amount} р`} /> },
        { key: 'bonus_display', label: 'Премия', render: (row) => <NumberCell value={row.bonus_display} /> },
    ]), [deductionState.saving, saveRowDeduction, state.data.permissions?.can_manage_deductions]);

    return (
        <div className="shell-page">
            <section className="shell-section shell-section--pageTop">
                <div className="shell-pageHeaderCompact shell-pageHeaderCompact--motivation">
                    <div className="shell-pageBandIntro">
                        <h3>Мотивация инженеров</h3>
                    </div>

                    <div className="shell-pageSummary shell-pageSummary--header shell-pageSummary--metricsWide">
                        {summaryCards.map((item) => (
                            <div key={item.label} className="shell-pageSummaryCard shell-pageSummaryCard--compact">
                                <span>{item.label}</span>
                                <strong className="shell-cellNumber">{item.value}</strong>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {state.warning ? <div className="shell-banner">{state.warning}</div> : null}

            <WeakCombinedTable
                rows={weakRows}
                emptyText="Слабых по темпу за период не найдено."
                onOpenHistory={setSelectedWeakRow}
            />

            <section className="shell-section">
                <div className="shell-sectionHeader">
                    <div>
                        <span className="shell-sectionEyebrow">Фильтры</span>
                        <h3>Период и локация</h3>
                    </div>
                </div>

                <form className="shell-searchForm shell-searchForm--compact" onSubmit={submitFilters}>
                    <label className="shell-field">
                        <span>Дата начала</span>
                        <input
                            type="date"
                            value={filters.start_date}
                            onChange={(event) => setFilters((current) => ({ ...current, start_date: event.target.value }))}
                        />
                    </label>
                    <label className="shell-field">
                        <span>Дата окончания</span>
                        <input
                            type="date"
                            value={filters.end_date}
                            onChange={(event) => setFilters((current) => ({ ...current, end_date: event.target.value }))}
                        />
                    </label>
                    <label className="shell-field">
                        <span>Локация</span>
                        <SearchableSelect
                            value={filters.location}
                            onChange={(value) => setFilters((current) => ({ ...current, location: value }))}
                            options={locations.map((item) => ({
                                value: item.city,
                                label: item.city,
                            }))}
                            placeholder="Все"
                        />
                    </label>
                    <div className="shell-searchActions">
                        <button
                            type="submit"
                            className="shell-actionButton shell-actionButton--primary"
                            disabled={state.loading || exportState.loading}
                        >
                            {state.loading ? 'Обновляем...' : 'Обновить'}
                        </button>
                        {state.data.permissions?.can_manage_deductions ? (
                            <button
                                type="button"
                                className="shell-actionButton shell-actionButton--secondary"
                                onClick={downloadExport}
                                disabled={state.loading || exportState.loading}
                            >
                                {exportState.loading ? 'Формируем...' : 'Скачать «Премия Hyosung»'}
                            </button>
                        ) : null}
                    </div>
                    {exportState.error ? (
                        <div className="shell-inlineMessage shell-inlineMessage--error">
                            {exportState.error}
                        </div>
                    ) : null}
                </form>
            </section>

            {state.data.permissions?.can_manage_deductions ? (
                <section className="shell-section">
                    <div className="shell-sectionHeader">
                        <div>
                            <span className="shell-sectionEyebrow">Данные для выплаты</span>
                            <h3>ИНН инженеров</h3>
                        </div>
                        <span>
                            {(state.data.engineer_payment_rows || []).filter((row) => !row.inn).length} без ИНН
                        </span>
                    </div>
                    <p className="shell-sectionDescription">
                        Инженеры приходят из внешней базы. ИНН сохраняется отдельно по ФИО и локации и автоматически подставляется в файл мотивации.
                    </p>
                    {(state.data.engineer_payment_rows || []).length ? (
                        <div className="shell-tableWrap">
                            <table className="shell-table">
                                <thead>
                                    <tr>
                                        <th>Инженер</th>
                                        <th>Локация</th>
                                        <th>Премия Hyosung</th>
                                        <th>ИНН</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(state.data.engineer_payment_rows || []).map((row) => {
                                        const rowKey = `${row.engineer}::${row.location}`;
                                        return (
                                            <tr key={rowKey}>
                                                <td>{row.engineer}</td>
                                                <td>{row.location}</td>
                                                <td><NumberCell value={row.bonus_display} /></td>
                                                <td>
                                                    <EngineerInnInput
                                                        row={row}
                                                        disabled={innState.savingKey === rowKey}
                                                        onSave={saveEngineerInn}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="shell-emptyState shell-emptyState--compact">
                            За выбранный период инженеров нет.
                        </div>
                    )}
                    {innState.error ? (
                        <div className="shell-inlineMessage shell-inlineMessage--error">{innState.error}</div>
                    ) : null}
                    {innState.message ? (
                        <div className="shell-inlineMessage">{innState.message}</div>
                    ) : null}
                </section>
            ) : null}

            <section className="shell-section">
                <div className="shell-sectionHeader">
                    <div>
                        <span className="shell-sectionEyebrow">Правила</span>
                        <h3>Нормы и вознаграждения</h3>
                    </div>
                </div>

                <div className="shell-pageSummary shell-pageSummary--compact shell-pageSummary--rules">
                    <RuleCard
                        title="Модули"
                        primary={`${formatValue(state.data.rules.modules.norm_minutes)} минут`}
                        secondary={`${formatValue(state.data.rules.modules.reward_amount)} р за модуль сверх нормы`}
                        note="Норма месяца = рабочие дни полного месяца × 8 × 60 / норма минут"
                    />
                    <RuleCard
                        title="Кассеты"
                        primary={`${formatValue(state.data.rules.cassettes.norm_per_day_units)} ед / день`}
                        secondary={`${formatValue(state.data.rules.cassettes.reward_amount)} р за кассету сверх нормы`}
                        note="Норма месяца = рабочие дни полного месяца × норма в день"
                    />
                    {(state.data.rules.overrides || []).map((item) => (
                        <RuleCard
                            key={`${item.location}-${item.category}`}
                            title={`${item.location} · ${item.category === 'modules' ? 'модули' : 'кассеты'}`}
                            primary={item.category === 'modules'
                                ? `${formatValue(item.norm_minutes)} минут`
                                : `${formatValue(item.norm_per_day_units)} ед / день`}
                            secondary={`${formatValue(item.reward_amount)} р`}
                        />
                    ))}
                </div>
            </section>

            {state.data.permissions?.can_manage_deductions ? (
                <section className="shell-section">
                    <div className="shell-sectionHeader">
                        <div>
                            <span className="shell-sectionEyebrow">Корректировка</span>
                            <h3>Вычет рабочих дней</h3>
                        </div>
                        {deductionState.message ? <span>{deductionState.message}</span> : null}
                    </div>
                    <div className="shell-searchForm shell-searchForm--compact">
                        <label className="shell-field">
                            <span>Вычесть всем</span>
                            <input
                                type="number"
                                min="0"
                                step="0.5"
                                value={deductionState.bulkValue}
                                onChange={(event) => setDeductionState((current) => ({ ...current, bulkValue: event.target.value }))}
                                placeholder="0"
                            />
                        </label>
                        <div className="shell-searchActions">
                            <button
                                type="button"
                                className="shell-actionButton shell-actionButton--primary"
                                onClick={applyBulkDeduction}
                                disabled={deductionState.saving || !uniqueDeductionRows().length}
                            >
                                {deductionState.saving ? 'Сохраняем...' : 'Применить ко всем'}
                            </button>
                        </div>
                    </div>
                </section>
            ) : null}

            <MotivationTable
                title="Модули"
                eyebrow="Расчет по модулям"
                rows={state.data.module_rows || []}
                emptyText="За выбранный период нет закрытых модулей."
                columns={moduleColumns}
            />

            <MotivationTable
                title="Кассеты"
                eyebrow="Расчет по кассетам"
                rows={state.data.cassette_rows || []}
                emptyText="За выбранный период нет закрытых кассет."
                columns={cassetteColumns}
            />

            <WeakPerformanceHistoryModal row={selectedWeakRow} onClose={() => setSelectedWeakRow(null)} />
        </div>
    );
}
